import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { iconsOptions } from '../../styles/cms.config';
import { ButtonsOptions, RatioOptions } from '@df/ui/atomic/atomic-options';
import { App } from '@df/core/app';
import { defaultImage } from '../../icms.config';

export namespace NPageFooter {
    export const BLOCK_ID = 'page-footer';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent {
        /**
         * Meganav menu
         */
        menus: IPageFooterMenu[];
        /**
         * Social services
         */
        socialServices: {
            /**
             * Item link
             */
            link: string;
            /**
             * Icon
             */
            icon: string;
        }[];
        /**
         * Bottom section links
         */
        bottomLinks: {
            /**
             * Item text
             */
            text: string;
            /**
             * Item link
             */
            link: string;
        }[];
        bannerHeading: string;
        bannerText: string;
        buttonCtaLink: string;
        buttonCtaCopy: string;
        buttonStyle?: string;
        background_image?: string;
        trustImage: string;
        trustImageLink: string;
        ratio?: string;
        payment_options: { payment_logo: string }[];
        workingHours?: IWorkingHours[];
        certifications?: IPageFooterBadgesIcons[];
        members?: IPageFooterBadgesIcons[];
    }

    export interface IPageFooterBadgesIcons {
        image: string;
        alt?: string;
    }

    interface IWorkingHours {
        text?: string;
    }

    interface IPageFooterMenu {
        heading: string;
        links: {
            /**
             * Item text
             */
            text: string;
            /**
             * Item link
             */
            link?: string;
            /**
             * Icon
             */
            icon?: string;
        }[];
    }
}

@Component({
    selector: 'page-footer',
    templateUrl: 'page-footer.html',
    styleUrls: ['page-footer.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageFooterComponent>({
    id: NPageFooter.BLOCK_ID,
    name: 'Page Footer',
    description: 'Page footer block',
    block: {
        url: NPageFooter.BLOCK_ID,
        scope: 'website',
        autoload: 'prerender'
    },
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'menus',
            title: 'Menus',
            itemName: 'menu',
            itemNameField: 'heading',
            maxSize: 5,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'heading',
                    title: 'Menu heading',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'items',
                    title: 'Menu items',
                    itemName: 'item',
                    required: true,
                    minSize: 2,
                    itemNameField: 'text',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Icon',
                            key: 'icon',
                            itemName: 'icon',
                            options: iconsOptions
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'text',
                            title: 'Text',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'link',
                            title: 'Link',
                            required: true
                        }
                    ]
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'workingHours',
            title: 'Working hours',
            itemName: 'workingHours',
            itemNameField: 'workingHours',
            maxSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'text',
                    title: 'Working hours'
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'bannerHeading',
            title: 'Banner heading',
            required: true,
            default: 'Apply for a Trade Account'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'bannerText',
            title: 'Banner text',
            required: true,
            default:
                'Torem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Curabitur tempus urna at turpis condimentum lobortis.'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'buttonCtaLink',
            title: 'Banner link',
            required: true,
            default: '/'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'buttonCtaCopy',
            title: 'Banner link text',
            required: true,
            default: 'Apply now'
        },
        {
            key: 'background_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'buttonStyle',
            title: 'Button style',
            options: ButtonsOptions,
            default: 'button-1'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'certifications',
            title: 'Certifications',
            itemName: 'certification',
            itemNameField: 'image',
            maxSize: 1,
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'alt',
                    title: 'Alt'
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'members',
            title: 'Members',
            itemName: 'member',
            itemNameField: 'image',
            maxSize: 1,
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'alt',
                    title: 'Alt'
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'socialServices',
            title: 'Social links',
            itemName: 'social link',
            itemNameField: 'link',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    key: 'icon',
                    itemName: 'icon',
                    options: iconsOptions,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link',
                    required: true
                }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'payment_options',
            title: 'Payment Options',
            itemName: 'Payment Option',
            itemNameField: 'payment_logo',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'payment_logo',
                    title: 'Icon',
                    options: [
                        { key: 'mc', title: 'Mastercard' },
                        { key: 'amex', title: 'Amex' },
                        { key: 'visa', title: 'Visa' },
                        { key: 'maestro', title: 'Maestro' },
                        { key: 'paypal', title: 'Paypal' },
                        { key: 'apple', title: 'Apple' },
                        { key: 'google', title: 'Google' },
                        { key: 'klarna', title: 'Klarna' }
                    ],
                    required: true
                }
            ]
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.image,
            key: 'trustImage',
            title: 'Trust Image',
            default: defaultImage,
            required: true
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'trustImageLink',
            title: 'Trust Image Link',
            required: true,
            default: '/'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'bottomLinks',
            title: 'Bottom Links',
            itemName: 'Link',
            itemNameField: 'text',
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        }
    ]
})
export class PageFooterComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageFooter.IBlockDataContent;

    protected app = inject(App);
}
